import type { ColummListColumn, ColumnFactory, ColumnFactoryList } from "../types";

export function createColumnFactoryList<RecordType, Key, Params, Ctx = void>(
    entries: ReadonlyArray<readonly [Key, ColumnFactory<RecordType, Params, Ctx>]>,
): ColumnFactoryList<RecordType, Key, Params, Ctx> {
    return new Map(entries);
}

type BuildColumnsFromDescriptorsParams<RecordType, Key, Params, Ctx = void> = {
    factoryList: ColumnFactoryList<RecordType, Key, Params, Ctx>;
    descriptors: readonly Params[];
    getKey: (descriptor: Params) => Key;
    fallback: ColumnFactory<RecordType, Params, Ctx>;
    ctx?: Ctx;
}

/**
 * Maps a list of backend column descriptors to concrete columns, selecting a factory per descriptor by key
 * and falling back to `fallback` for unknown keys. Factories may return one column or several, so results
 * are flattened — callers don't need to special-case the array shape of `ColumnFactory`.
 */
export function buildColumnsFromDescriptors<RecordType, Key, Params, Ctx = void>({
    factoryList,
    descriptors,
    getKey,
    fallback,
    ctx,
}: BuildColumnsFromDescriptorsParams<RecordType, Key, Params, Ctx>): ReadonlyArray<ColummListColumn<RecordType>> {
    return descriptors.flatMap((descriptor) => {
        const factory = factoryList.get(getKey(descriptor)) ?? fallback;

        const result = factory(descriptor, ctx);

        return Array.isArray(result) ? result : [result];
    });
}